import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, Button, Card, ConfirmDialog, PillButton, pillClasses, apiErrorMessage } from '@aralere/shared-ui';
import type { ClientDocument } from '@aralere/shared-types';

// Client "Documents" page (spec §18; Stage-3 slice 7 inc 2 — s43 §11, Q45). Shows ONLY what the
// server already resolved as visible: files shared by us (per-file grants) plus files the client
// uploaded themselves. While the client has an ACTIVE project they can upload to it and delete
// their own uploads; everything else is read-only. Capability booleans (canView / canDownload /
// downloadOnPayment / watermarkPreview) arrive pre-resolved — the page never re-derives access.

const inputClass = 'w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]';

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** ISO timestamp -> MM-DD-YYYY. */
function displayDate(iso: string | null): string {
  if (!iso) return '-';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${m}-${d}-${y}`;
}

export function ClientDocuments(): JSX.Element {
  const [docs, setDocs] = useState<ClientDocument[]>([]);
  const [canUpload, setCanUpload] = useState(false);
  const [activeProjects, setActiveProjects] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [uploadProjectId, setUploadProjectId] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);

  // Deep link from the "document ready" notification (linkTo=/documents?doc=<id>): once loaded,
  // scroll the matching row into view and briefly highlight it.
  const [searchParams] = useSearchParams();
  const focusDocId = searchParams.get('doc');
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  useEffect(() => {
    if (!focusDocId || loading) return;
    const el = rowRefs.current.get(focusDocId);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [focusDocId, loading, docs]);

  const [deleteTarget, setDeleteTarget] = useState<ClientDocument | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Watermarked previews render on first open and can take up to ~2 min; we gate them behind a
  // dialog whose confirm button carries the user activation needed to open a new tab (so the
  // popup blocker never fires — see the synchronous window.open in startPreview).
  const [previewTarget, setPreviewTarget] = useState<{ doc: ClientDocument; mode: 'view' | 'download' } | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);

  async function load(): Promise<void> {
    try {
      const r = await api.account.listMyDocuments();
      setDocs(r.documents);
      setCanUpload(r.canUpload);
      setActiveProjects(r.activeProjects);
      setUploadProjectId((prev) => (r.activeProjects.some((p) => p.id === prev) ? prev : r.activeProjects[0]?.id ?? ''));
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function doUpload(files: FileList | null): Promise<void> {
    if (!files || files.length === 0 || !uploadProjectId) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        await api.account.uploadMyDocument(uploadProjectId, file);
      }
      await load();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  async function doDelete(): Promise<void> {
    if (!deleteTarget) return;
    setDeleting(true);
    setError(null);
    try {
      await api.account.deleteMyDocument(deleteTarget.id, deleteTarget.ownerId);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      setError(apiErrorMessage(e));
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  }

  /**
   * Fetch a watermarked preview and open/download it. Run from the preview dialog's confirm
   * button so this executes inside a real user activation — for 'view' we open the blank tab
   * SYNCHRONOUSLY (before the await) so the popup blocker doesn't fire, then point it at the blob
   * once the render finishes. 'download' needs no popup.
   */
  async function startPreview(): Promise<void> {
    if (!previewTarget) return;
    const { doc, mode } = previewTarget;
    // Open the tab NOW, within the click's activation window. It will show blank/loading until we
    // set its location. If the fetch fails we close it.
    // Open the tab NOW, within the click's activation window, so the popup blocker doesn't fire.
    // NOTE: no 'noopener' here — that flag makes window.open return null, which previously caused
    // the fallback same-tab navigation. We get the handle, then sever opener access for safety.
    const tab = mode === 'view' ? window.open('about:blank', '_blank') : null;
    if (tab) {
      try {
        tab.opener = null;
      } catch {
        /* some browsers disallow setting opener; the tab is still isolated enough for a blob URL */
      }
    }
    setPreviewBusy(true);
    setError(null);
    try {
      const res = await fetch(api.account.myDocumentFileUrl(doc.id, doc.ownerId, mode === 'download'));
      if (!res.ok) {
        throw new Error(res.status === 423 ? 'This file is not available yet.' : 'Could not open this file. Please try again.');
      }
      const blob = await res.blob();
      // Wrap in a named File so browsers that read it (e.g. Chrome's PDF viewer Save action) use
      // the clean filename rather than the blob UUID. The tab TITLE for a blob-opened PDF is still
      // browser-controlled and may show the object id — that's a platform limitation, not our name.
      const named = new File([blob], doc.fileName, { type: blob.type });
      const url = URL.createObjectURL(named);
      if (mode === 'download') {
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else if (tab) {
        tab.location.href = url;
      } else {
        // Tab was blocked despite the activation; don't hijack the current tab (that would drop
        // them out of the portal). Tell them to allow popups and try again.
        URL.revokeObjectURL(url);
        throw new Error('Your browser blocked the preview tab. Please allow pop-ups for this site and try again.');
      }
      setTimeout(() => URL.revokeObjectURL(url), 120000);
      setPreviewTarget(null);
    } catch (e) {
      if (tab) tab.close();
      setError(apiErrorMessage(e));
      setPreviewTarget(null);
    } finally {
      setPreviewBusy(false);
    }
  }

  // Group: project docs under their project name, client-scope docs under "Shared with you".
  const groups = useMemo(() => {
    const map = new Map<string, ClientDocument[]>();
    for (const d of docs) {
      const key = d.scope === 'project' ? `Project: ${d.projectName ?? 'Project'}` : 'Shared with you';
      const list = map.get(key) ?? [];
      list.push(d);
      map.set(key, list);
    }
    return Array.from(map.entries());
  }, [docs]);

  if (loading) return <div className="p-6 text-sm text-[var(--color-muted)]">Loading…</div>;

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-4 md:p-6">
      <h1 className="text-xl font-semibold text-[var(--color-text)]">Documents</h1>

      {canUpload ? (
        <Card>
          <div className="flex flex-wrap items-end gap-3 p-4">
            <div className="min-w-[220px]">
              <label className="mb-1 block text-sm font-medium text-[var(--color-text)]">Project</label>
              <select className={inputClass} value={uploadProjectId} onChange={(e) => setUploadProjectId(e.target.value)}>
                {activeProjects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="pb-0.5">
              <Button onClick={() => fileInput.current?.click()} disabled={uploading || !uploadProjectId}>
                {uploading ? 'Uploading…' : 'Upload files'}
              </Button>
              <input ref={fileInput} type="file" multiple className="hidden" onChange={(e) => void doUpload(e.target.files)} />
            </div>
            <p className="pb-1.5 text-xs text-[var(--color-muted)]">
              PDF, images, Office files, ZIP, or text. 25 MB per file. You can remove files you uploaded while the project is active.
            </p>
          </div>
        </Card>
      ) : null}

      {error ? <div className="rounded-md bg-[var(--tint-red-bg-soft)] p-3 text-sm text-[var(--tint-red-text)]">{error}</div> : null}

      {docs.length === 0 ? (
        <p className="text-sm text-[var(--color-muted)]">No documents have been shared with you yet.</p>
      ) : (
        groups.map(([groupName, groupDocs]) => (
          <div key={groupName} className="space-y-2">
            <h2 className="text-sm font-semibold text-[var(--color-muted)]">{groupName}</h2>
            {groupDocs.map((d) => (
              <div
                key={d.id}
                ref={(el) => {
                  if (el) rowRefs.current.set(d.id, el);
                  else rowRefs.current.delete(d.id);
                }}
                className={
                  d.id === focusDocId
                    ? 'rounded-xl ring-2 ring-[var(--tint-blue-text)] ring-offset-2'
                    : d.isNew
                      ? 'rounded-xl ring-2 ring-[var(--tint-blue-border)] ring-offset-2'
                      : undefined
                }
              >
                <Card>
                <div className="flex flex-wrap items-start gap-3 p-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-[var(--color-text)]">{d.title}</span>
                      {d.folder ? <span className="rounded-full bg-[var(--tint-blue-bg-soft)] px-2 py-0.5 text-xs text-[var(--tint-blue-text)]">Folder: {d.folder}</span> : null}
                      {d.signed ? <span className="rounded-full bg-[var(--tint-green-bg)] px-2 py-0.5 text-xs text-[var(--tint-green-text)]">Signed{d.signedDate ? ` ${displayDate(d.signedDate)}` : ''}</span> : null}
                      {d.uploadedByMe ? <span className="rounded-full bg-[var(--tint-slate-bg)] px-2 py-0.5 text-xs text-[var(--tint-slate-text)]">Uploaded by you</span> : null}
                      {d.downloadOnPayment ? <span className="rounded-full bg-[var(--tint-warn-bg)] px-2 py-0.5 text-xs text-[var(--tint-warn-text)]">Original copy available after payment</span> : null}
                      {d.watermarkPreview ? <span className="rounded-full bg-[var(--tint-warn-bg)] px-2 py-0.5 text-xs text-[var(--tint-warn-text)]">Preview available before payment</span> : null}
                    </div>
                    <div className="mt-1 text-xs text-[var(--color-muted)]">
                      {d.fileName} · {formatBytes(d.sizeBytes)} · {displayDate(d.createdAt)}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {d.canView ? (
                      d.watermarkPreview ? (
                        <PillButton onClick={() => setPreviewTarget({ doc: d, mode: 'view' })}>
                          Open
                        </PillButton>
                      ) : (
                        <a
                          className={pillClasses()}
                          href={api.account.myDocumentFileUrl(d.id, d.ownerId)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Open
                        </a>
                      )
                    ) : null}
                    {d.canDownload ? (
                      d.watermarkPreview ? (
                        <PillButton onClick={() => setPreviewTarget({ doc: d, mode: 'download' })}>
                          Download
                        </PillButton>
                      ) : (
                        <a
                          className={pillClasses()}
                          href={api.account.myDocumentFileUrl(d.id, d.ownerId, true)}
                        >
                          Download
                        </a>
                      )
                    ) : null}
                    {d.uploadedByMe && !d.signed ? (
                      <PillButton tone="red" onClick={() => setDeleteTarget(d)}>
                        Delete
                      </PillButton>
                    ) : null}
                  </div>
                </div>
              </Card>
              </div>
            ))}
          </div>
        ))
      )}

      {previewTarget ? (
        <ConfirmDialog
          open={true}
          title={previewTarget.mode === 'download' ? 'Download Preview' : 'Open Preview'}
          confirmLabel={previewBusy ? 'Preparing…' : previewTarget.mode === 'download' ? 'Download Preview' : 'Open Preview'}
          busy={previewBusy}
          onConfirm={startPreview}
          onCancel={() => (previewBusy ? undefined : setPreviewTarget(null))}
        >
          <p>
            {previewTarget.mode === 'download'
              ? 'You are downloading a preview. Depending on the size of the file, this may take up to 2 minutes.'
              : 'You are opening a preview. Depending on the size of the file, this may take up to 2 minutes.'}
          </p>
        </ConfirmDialog>
      ) : null}

      {deleteTarget ? (
        <ConfirmDialog
          open={true}
          title="Delete file?"
          confirmLabel="Delete"
          confirmVariant="danger"
          busy={deleting}
          onConfirm={doDelete}
          onCancel={() => setDeleteTarget(null)}
        >
          <p>Delete "{deleteTarget.title}"? This cannot be undone.</p>
        </ConfirmDialog>
      ) : null}
    </div>
  );
}
